<template>
  <div
    v-track="{ product, index, listName: listTitle }"
    class="product-spot-container"
    :class="{
      'product-spot-regular': !large,
      'product-spot-large': large,
      'mr-12': isMobile && !isMostright,
      'mr-24': !isMobile && !isMostright,
    }"
  >
    <meta
      v-if="useSchemaTags"
      itemprop="url"
      :content="route"
    >
    <div
      id="productSpot"
      :ecom-id="shownVariant.ecomId"
      :itemscope="useSchemaTags"
      :itemtype="useSchemaTags ? 'https://schema.org/Product' : undefined"
    >
      <meta
        v-if="useSchemaTags"
        itemprop="description"
        :content="shownVariant.description"
      >
      <meta
        v-if="useSchemaTags"
        itemprop="image"
        :content="shownVariant.standardImage"
      >
      <meta
        v-if="useSchemaTags"
        itemprop="sku"
        :content="shownVariant.sku"
      >
      <meta
        v-if="useSchemaTags"
        itemprop="mpn"
        :content="shownVariant.sku"
      >
      <div
        v-if="useSchemaTags"
        itemprop="brand"
        itemtype="https://schema.org/Brand"
        itemscope
      >
        <meta itemprop="name" :content="shownVariant.brandName">
      </div>

      <div v-if="shownVariant.productImages.length > 1">
        <SimpleSwiper
          :images="shownVariant.productImages"
          :contain="true"
          :to="route"
          :alt="shownVariant.title"
          preset="standard"
          sizes="mob375:100vw mob390:100vw mob414:100vw desk:309px"
        >
          <div
            v-if="language && shownVariant.seal && sealName in shownVariant.seal"
            class="flex items-center justify-center rounded-full h-70 w-70 absolute bottom-12 transform right-12  z-above"
          >
            <img :src="shownVariant.seal[sealName].url" class="h-full w-full absolute top-0 left-0">
          </div>
        </SimpleSwiper>
      </div>
      <div v-else>
        <nuxt-link
          class="relative flex justify-center items-center cursor-pointer"
          :to="route"
          :style="`background: ${background}`"
          :itemprop="useSchemaTags ? 'url' : false"
          @click="clickLink"
        >
          <div class="block h-full w-full">
            <img
              :src="shownVariant.standardImage"
              class="object-center object-cover h-full w-full"
              :class="{
                'opacity-50': !shownVariant.available && !shownVariant.allowStockMonitoring,
              }"
              :alt="shownVariant.title"
              height="128"
              width="128"
              :itemprop="useSchemaTags ? 'image' : undefined"
            >
            <div
              v-if="language && shownVariant.seal && sealName in shownVariant.seal"
              class="flex items-center justify-center rounded-full h-70 w-70 absolute bottom-12 transform right-12  z-above"
            >
              <img :src="shownVariant.seal[sealName].url" class="h-full w-full absolute top-0 left-0">
            </div>
          </div>
        </nuxt-link>
      </div>
      <div
        class="pt-16 pb-40 relative"
        :class="{
          'px-12': !large,
          'px-24': large,
        }"
      >
        <div v-if="shownVariant.hasFlags" class="md:flex">
          <div
            v-for="(flag, idx) in productFlag"
            :key="idx"
            class="
              inline-block
              bg-black-25
              text-black-75 text-xs
              rounded-regular
              w-fit-content
              py-4
              px-8
              mb-12
              mr-8
              font-medium
              hidden
              md:block

            "
          >
            <span v-if="flag.translate">
              {{ $lang('product_page', flag.value) }}
            </span>
            <span v-else>
              {{ flag.value }}
            </span>
          </div>
        </div>
        <div class="flex justify-between">
          <div>
            <nuxt-link
              :to="route"
              :class="{
                'text-product-name': !large,
                'text-price-large': large,
              }"
              :itemprop="useSchemaTags ? 'url' : false"
              @click="clickLink"
            >
              <span :itemprop="useSchemaTags ? 'name' : undefined">
                {{ shownVariant.title }}
              </span>
            </nuxt-link>
            <div v-show="!large">
              <div
                v-if="shownVariant.showAsOnSale"
                :itemprop="useSchemaTags ? 'offers' : false"
                :itemscope="useSchemaTags"
                :itemtype="useSchemaTags ? 'https://schema.org/Offer' : false"
                class="mt-4"
              >
                <meta
                  v-if="useSchemaTags"
                  itemprop="priceValidUntil"
                  :content="validUntil"
                >
                <meta
                  v-if="useSchemaTags"
                  itemprop="priceCurrency"
                  :content="currencyCode"
                >
                <meta
                  v-if="useSchemaTags"
                  itemprop="url"
                  :content="itemPath"
                >
                <meta
                  v-if="useSchemaTags"
                  itemprop="availability"
                  :content="
                    shownVariant.available
                      ? 'https://schema.org/InStock'
                      : 'https://schema.org/SoldOut'
                  "
                >
                <span
                  class="text-price text-warning mr-2 sm:mr-8"
                  :itemprop="useSchemaTags ? 'price' : false"
                  :content="shownVariant.priceAsNumber"
                >
                  {{ shownVariant.price }}
                </span>
                <span
                  class="text-price line-through text-muted"
                  :itemprop="useSchemaTags ? 'price' : false"
                  :content="shownVariant.priceBeforeDiscountAsNumber"
                >
                  {{ shownVariant.priceBeforeDiscount }}
                </span>
              </div>
              <div
                v-else
                :itemprop="useSchemaTags ? 'offers' : false"
                :itemscope="useSchemaTags"
                :itemtype="useSchemaTags ? 'https://schema.org/Offer' : false"
                class="mt-4"
              >
                <meta
                  v-if="useSchemaTags"
                  itemprop="priceValidUntil"
                  :content="validUntil"
                >
                <meta
                  v-if="useSchemaTags"
                  itemprop="priceCurrency"
                  :content="currencyCode"
                >
                <meta
                  v-if="useSchemaTags"
                  itemprop="url"
                  :content="itemPath"
                >
                <meta
                  v-if="useSchemaTags"
                  itemprop="availability"
                  :content="
                    shownVariant.available
                      ? 'https://schema.org/InStock'
                      : 'https://schema.org/SoldOut'
                  "
                >
                <span
                  class="bold"
                  itemprop="price"
                  :content="shownVariant.priceAsNumber"
                >
                  {{ shownVariant.price }}
                </span>
              </div>
            </div>
            <div v-if="!shownVariant.hideSizeChoice" class="flex flex-wrap mt-8 text-muted">
              <div
                v-for="item in shownVariant.items"
                :key="'product-spot-size-' + item.item"
                class="text-size-chart mr-8"
                :class="{
                  'text-muted': item.stock === 'yes',
                  'text-black-50 line-through': item.stock === 'no',
                }"
              >
                {{ item.name }}
              </div>
            </div>
          </div>
          <div v-if="large">
            <div
              v-if="shownVariant.showAsOnSale"
              :class="{
                'text-price': !large,
                'text-price-large': large,
              }"
            >
              <div class="text-warning mb-4" :content="shownVariant.priceAsNumber">
                {{ shownVariant.price }}
              </div>
              <div class="line-through text-muted">
                {{ shownVariant.priceBeforeDiscount }}
              </div>
            </div>
            <div
              v-else
              :class="{
                'text-price': !large,
                'text-price-large': large,
              }"
            >
              {{ shownVariant.price }}
            </div>
          </div>
        </div>
        <div v-if="!shownVariant.hideColorChoice" class="float-left flex mt-12">
          <div
            v-for="variant in productVariants"
            :key="'product-spot-variant-' + variant.itemId"
            class="h-16 w-16 border rounded-full mr-4 md:mr-8"
            :class="{
              'border-transparent': variant.itemId !== shownVariant.itemId,
              'border-muted': variant.itemId === shownVariant.itemId,
            }"
          >
            <div
              class="border border-white rounded-full h-full w-full cursor-pointer"
              :style="`background: ${
                variant.color.desc === 'Multi' ? 'linear-gradient(#052f27, #c94912, #569fc6)' : variant.color.hex
              }`"
              @click="changeColor(variant.ecomId);"
            />
          </div>
        </div>
        <div v-if="yotpoReview && yotpoReview.product_score" class="mt-8 float-right text-right">
          <ReviewStars
            :minimum-score="4"
            :score="yotpoReview.product_score"
            :placement="'spot'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Product from '~/models/product';
import { useConfigStore } from '~/store/config';
import type { Currencies } from '~/constants/types/cart';
import { useCartStore } from '~/store/cart';

import ReviewStars from '~/components/productlist/ReviewStars.vue';
import { useUiStore } from '~/store/ui';
import SimpleSwiper from '~/components/global/SimpleSwiper.vue';
import dayjs from 'dayjs';
import { useProductImpressionTracking as vTrack, pushProductClick } from '~/composables/useTracking';

const uiStore = useUiStore();

const { fetchProduct } = useProductService();

const config = useConfigStore();
const cartStore = useCartStore();
const runtimeConfig = useRuntimeConfig();
const router = useRouter();

const colorLangList = {
  blue: ['Blå', 'Sinninen', 'Blå', 'Blå'],
  green: ['Grön', 'Vihreä', 'Grønn', 'Grön'],
  red: ['Röd', 'Punainen', 'Rød', 'Röd'],
};

const props = defineProps<{
  product: Product,
  index: number,
  bgcolor?: string,
  solo?: boolean,
  isMobile?: boolean,
  isMostright?: boolean,
  useSchemaTags?: boolean,
  large?: boolean,
  listTitle?: string,
  language?: string,
}>();

const shownVariant = ref(props.product);
const loadingStatus = ref('INIT');
const windowWidth = ref(0);

/**
 * @todo
 */
const yotpoReview = computed<any>(()=> {
  return null;
  //return config.yotpoReviewByProductSku(shownVariant?.sku.value);
});

const route = computed<string>(()=> {
  const route = config.getProductRoute(shownVariant.value.ecomId);
  return route ? route.route : '/';
});

const currencyCode = computed<string>(()=> {
  const fallBack = shownVariant.value?.price?.replace(
    /[^a-zA-Z]+/gi,
    ''
  ) as Currencies;
  return cartStore.cartData.currency || fallBack;
});

const validUntil = computed(()=> {
  return dayjs().add(1, 'days').toISOString();
});

const itemPath = computed<string>(()=> {
  return `${runtimeConfig.public.frontendUrl}${route}`;
});

const parentComponentTitle = computed<string>(()=> {
  if (props.listTitle) {
    return props.listTitle;
  }

  return '';
  /**
   * @todo
   */
  /*
  const parent = $parent.value as { component: ComponentBlock } & Vue;
  const parentRoute = config.routeData($route.path.value);
  const fallBackPageName = parentRoute ? parentRoute.title : '';

  return parent.component ? parent.component.title : fallBackPageName;*/
});

const productVariants = computed<Product[]>(()=> {
  const uniqueBy = (k, s = new Set) => o => !s.has(o[k]) && s.add(o[k]);
  const variants = shownVariant.value.variants;
  return variants
    .filter((p) => !p.isArchived)
    .filter((p) => p.ecomId == shownVariant.ecomId || p.centraVariant != shownVariant.centraVariant)
    .filter(uniqueBy('centraVariant'))
    .reverse();
});

const productFlag = computed<object[]>(()=> {
  return shownVariant.value.flags.slice(0, 2);
});

const background = computed<string>(()=> {
  if (props.bgcolor) {
    return props.bgcolor;
  } else if (colorLangList.blue.includes(shownVariant.value.color.desc)) {
    return '#DCE1E6';
  } else if (colorLangList.red.includes(shownVariant.value.color.desc)) {
    return '#F3E8E2';
  } else if (colorLangList.green.includes(shownVariant.value.color.desc)) {
    return '#DCDFDC';
  } else {
    return '#EAE9E8';
  }
});

const sealName = computed<string>(()=> {
  return `${props.language?.toLowerCase()}_img`;
});

const clickLink = () => {
  pushProductClick(shownVariant.value, parentComponentTitle.value);
  //router.push(route.value)
};

const changeColor = async(variantEcomId: string | number) => {
  try {
    const { ecomData, contentData } = await fetchProduct(variantEcomId);
    shownVariant.value = new Product({ ecomData, contentData });
  } catch (e) {
    console.error(e);
    uiStore.setError(e.response.status);
  }
};

</script>

<style scoped>
.product-spot-container:hover .stars {
  opacity: 1;
}

.product-spot-regular {
  width: calc(50% - 6px);

  @screen md {
    width: calc(25% - 18px);
  }
}

.product-spot-large {
  width: 100%;
}

.product-spot-regular:nth-child(4n) {
  @screen lg {

  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.buy-button:hover {
  .buy-label {
    opacity: 1;
  }
}

.buy-button .buy-label--show {
  opacity: 1;
}

.buy-label-flerp {
  left: 77%;
  margin-bottom: -7px;

  @screen xl {
    left: 46%;
  }
}

.swiper-button-next,
.swiper-button-prev {
  @apply h-40 w-40 rounded-full bg-secondary;
  display: none;

  &:after {
    @apply hidden;
  }
}

.swiper-container:hover .swiper-button-next,
.swiper-container:hover .swiper-button-prev {
  @screen md {
    display: flex;
  }
}

.swiper-pagination-bullet:only-child {
  visibility: hidden;
}
</style>
